import { useState, useCallback } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Calendar,
  Plus,
  Loader2,
  Trash2,
  Edit,
  Search,
  CalendarClock,
  Building2,
  Globe,
  ChevronLeft,
  ChevronRight,
  Check,
  RefreshCw,
  CalendarDays,
  Clock,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useBankHolidays, useBankHolidayActions } from '@/hooks/useBankHolidays';
import { motion } from 'framer-motion';

// Stat Pill Component
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" }
  };
  const cfg = colorConfig[color];

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${cfg.bg} border border-slate-200/50`}>
      <div className={`p-2 rounded-lg ${cfg.icon}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className={`text-xl font-bold ${cfg.value}`}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
};

// Holiday Row Component
const HolidayRow = ({ holiday, formatDate, getRegionLabel, onEdit, onDelete }) => {
  const sourceConfig = {
    gov_uk: { accent: "bg-indigo-500", badge: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    custom: { accent: "bg-violet-500", badge: "bg-violet-50 text-violet-700 border-violet-200" },
    space: { accent: "bg-sky-500", badge: "bg-sky-50 text-sky-700 border-sky-200" }
  };

  const source = holiday.source === 'gov_uk' ? 'gov_uk' : (holiday.project_id ? 'space' : 'custom');
  const cfg = sourceConfig[source];

  return (
    <div className={`group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-md hover:border-slate-300 ${holiday.is_past ? 'opacity-60' : ''}`}>
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${cfg.accent} rounded-full`} />

      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-xl ${holiday.is_past ? 'bg-slate-100' : 'bg-slate-50'}`}>
          <Calendar className={`h-5 w-5 ${holiday.is_past ? 'text-slate-400' : 'text-slate-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className={`font-semibold ${holiday.is_past ? 'text-slate-500' : 'text-slate-800'}`}>
              {holiday.name}
            </h3>

            {holiday.source === 'gov_uk' ? (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${cfg.badge}`}>
                <Globe className="h-3 w-3" />
                {getRegionLabel(holiday.country_code)}
              </span>
            ) : holiday.project_id ? (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${cfg.badge}`}>
                <Building2 className="h-3 w-3" />
                {holiday.space_name || 'Space'}
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${cfg.badge}`}>
                <Sparkles className="h-3 w-3" />
                Custom
              </span>
            )}

            {holiday.recurring && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Recurring
              </span>
            )}

            {holiday.is_past && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                Past
              </span>
            )}

            {holiday.is_upcoming && !holiday.is_past && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Upcoming
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <CalendarClock className="h-4 w-4" />
            <span>{formatDate(holiday.date)}</span>
          </div>
        </div>

        {holiday.source !== 'gov_uk' && (
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={() => onEdit(holiday)} className="h-8 w-8 p-0 rounded-lg">
              <Edit className="h-4 w-4 text-slate-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(holiday)} className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const BankHolidaysPage = () => {
  const { userProfile } = useAuth();

  usePageTitle('Bank Holidays');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [regionsDialogOpen, setRegionsDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    recurring: false,
    applyTo: 'company',
    selectedSpaces: []
  });
  const [selectedRegions, setSelectedRegions] = useState([]);

  const {
    holidays,
    spaces,
    stats,
    subscribedRegions,
    loading,
    error,
    pagination,
    filters,
    formatDate,
    refetch,
    years,
    ukRegions
  } = useBankHolidays(userProfile?.company_id);

  const {
    createHoliday,
    updateHoliday,
    deleteHoliday,
    updateRegionSubscriptions,
    processing
  } = useBankHolidayActions(userProfile?.company_id, refetch);

  const openRegionsDialog = useCallback(() => {
    setSelectedRegions([...subscribedRegions]);
    setRegionsDialogOpen(true);
  }, [subscribedRegions]);

  const openCreateDialog = useCallback(() => {
    setEditingHoliday(null);
    setFormData({ name: '', date: '', recurring: false, applyTo: 'company', selectedSpaces: [] });
    setDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      recurring: holiday.recurring || false,
      applyTo: holiday.project_id ? 'spaces' : 'company',
      selectedSpaces: holiday.project_id ? [holiday.project_id] : []
    });
    setDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  }, []);

  const toggleSpace = useCallback((spaceId) => {
    setFormData(prev => ({
      ...prev,
      selectedSpaces: prev.selectedSpaces.includes(spaceId)
        ? prev.selectedSpaces.filter(id => id !== spaceId)
        : [...prev.selectedSpaces, spaceId]
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData.name.trim() || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.applyTo === 'spaces' && formData.selectedSpaces.length === 0) {
      toast.error('Please select at least one space');
      return;
    }

    if (editingHoliday) {
      const result = await updateHoliday(editingHoliday.id, formData);
      if (result.success) {
        toast.success('Bank holiday updated');
        setDialogOpen(false);
        setEditingHoliday(null);
      } else {
        toast.error(result.error || 'Failed to update');
      }
    } else {
      const result = await createHoliday(formData);
      if (result.success) {
        toast.success(formData.applyTo === 'company' ? 'Holiday created for company' : `Holiday created for ${formData.selectedSpaces.length} space(s)`);
        setDialogOpen(false);
      } else {
        toast.error(result.error || 'Failed to create');
      }
    }
  }, [formData, editingHoliday, createHoliday, updateHoliday]);

  const handleDelete = useCallback(async () => {
    if (!holidayToDelete) return;

    const result = await deleteHoliday(holidayToDelete.id);
    if (result.success) {
      toast.success('Bank holiday deleted');
      setDeleteDialogOpen(false);
      setHolidayToDelete(null);
    } else {
      toast.error(result.error || 'Failed to delete');
    }
  }, [holidayToDelete, deleteHoliday]);

  const handleUpdateRegions = useCallback(async () => {
    const result = await updateRegionSubscriptions(selectedRegions);
    if (result.success) {
      toast.success('Region subscriptions updated');
      setRegionsDialogOpen(false);
    } else {
      toast.error(result.error || 'Failed to update');
    }
  }, [selectedRegions, updateRegionSubscriptions]);

  const toggleRegion = useCallback((regionValue) => {
    setSelectedRegions(prev =>
      prev.includes(regionValue)
        ? prev.filter(r => r !== regionValue)
        : [...prev, regionValue]
    );
  }, []);

  const getSpaceName = useCallback((projectId) => {
    if (!projectId) return null;
    const space = spaces.find(s => s.id === projectId);
    return space?.name || 'Unknown Space';
  }, [spaces]);

  const getRegionLabel = useCallback((regionValue) => {
    const region = ukRegions.find(r => r.value === regionValue);
    return region?.label || regionValue;
  }, [ukRegions]);

  if (loading && !holidays.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading holidays...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-rose-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-rose-500" />
          </div>
          <p className="text-slate-800 font-semibold">Failed to load holidays</p>
          <p className="text-slate-500 text-sm mt-1">{error}</p>
          <Button onClick={refetch} className="mt-4 rounded-full">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Bank Holidays</h1>
            <p className="text-slate-500 mt-1">UK bank holidays sync automatically from Gov.uk daily</p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openRegionsDialog}
              className={`h-10 rounded-full px-5 ${subscribedRegions.length > 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-slate-200'}`}
            >
              {subscribedRegions.length > 0 ? (
                <><Check className="h-4 w-4 mr-2" />Gov.uk Sync</>
              ) : (
                <><Globe className="h-4 w-4 mr-2" />Gov.uk Sync</>
              )}
            </Button>
            <Button onClick={openCreateDialog} className="h-10 bg-slate-800 hover:bg-slate-700 rounded-full px-5">
              <Plus className="h-4 w-4 mr-2" />
              Add Custom Holiday
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatPill icon={CalendarDays} label="Total Holidays" value={stats.total} color="slate" />
          <StatPill icon={Calendar} label="This Year" value={stats.this_year} color="indigo" />
          <StatPill icon={Clock} label="Upcoming" value={stats.upcoming} color="emerald" />
          <StatPill icon={Globe} label="Company-Wide" value={stats.company_wide} color="amber" />
        </div>
      </motion.div>

      {/* Holidays List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search holidays..."
              value={filters.search}
              onChange={(e) => filters.setSearch(e.target.value)}
              className="pl-10 h-10 rounded-full border-slate-200"
            />
          </div>
          <Select value={filters.year} onValueChange={filters.setYear}>
            <SelectTrigger className="w-full sm:w-[130px] h-10 rounded-full border-slate-200">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((year) => (
                <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.space} onValueChange={filters.setSpace}>
            <SelectTrigger className="w-full sm:w-[150px] h-10 rounded-full border-slate-200">
              <SelectValue placeholder="Space" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Spaces</SelectItem>
              {spaces.map((space) => (
                <SelectItem key={space.id} value={space.id}>{space.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            Showing {holidays.length} of {pagination.filteredItems} holidays
            {loading && <Loader2 className="inline h-4 w-4 ml-2 animate-spin" />}
          </p>
        </div>

        {/* Holidays */}
        {holidays.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-slate-50 flex items-center justify-center">
              <Calendar className="h-7 w-7 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No bank holidays found</p>
            <p className="text-slate-400 text-sm mt-1">Create your first bank holiday to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {holidays.map((holiday) => (
              <HolidayRow
                key={holiday.id}
                holiday={holiday}
                formatDate={formatDate}
                getRegionLabel={getRegionLabel}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className="rounded-full"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="rounded-full"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Bank Holiday' : 'Add Bank Holiday'}</DialogTitle>
            <DialogDescription>
              {editingHoliday ? 'Update the bank holiday details.' : 'Create a new bank holiday for your company or specific spaces.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input
                placeholder="e.g. New Year's Day"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
              <Checkbox
                id="recurring"
                checked={formData.recurring}
                onCheckedChange={(checked) => setFormData({ ...formData, recurring: checked })}
              />
              <Label htmlFor="recurring" className="cursor-pointer">Recurring yearly</Label>
            </div>

            {/* Apply To */}
            <div className="space-y-3 pt-2">
              <Label>Apply To *</Label>
              <div className="space-y-2">
                <div
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.applyTo === 'company' ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setFormData({ ...formData, applyTo: 'company', selectedSpaces: [] })}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.applyTo === 'company' ? 'border-slate-800' : 'border-slate-300'}`}>
                    {formData.applyTo === 'company' && <div className="w-2 h-2 rounded-full bg-slate-800" />}
                  </div>
                  <Globe className="h-5 w-5 text-slate-600" />
                  <div>
                    <p className="font-medium text-slate-800">Entire Company</p>
                    <p className="text-xs text-slate-500">Holiday applies to all spaces</p>
                  </div>
                </div>

                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.applyTo === 'spaces' ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setFormData({ ...formData, applyTo: 'spaces' })}
                >
                  <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${formData.applyTo === 'spaces' ? 'border-slate-800' : 'border-slate-300'}`}>
                    {formData.applyTo === 'spaces' && <div className="w-2 h-2 rounded-full bg-slate-800" />}
                  </div>
                  <Building2 className="h-5 w-5 text-slate-600" />
                  <div>
                    <p className="font-medium text-slate-800">Specific Spaces</p>
                    <p className="text-xs text-slate-500">Choose which spaces this holiday applies to</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Space Selection */}
            {formData.applyTo === 'spaces' && (
              <div className="space-y-2">
                <Label>Select Spaces *</Label>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-3 space-y-2">
                  {spaces.map((space) => (
                    <div key={space.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                      <Checkbox
                        id={space.id}
                        checked={formData.selectedSpaces.includes(space.id)}
                        onCheckedChange={() => toggleSpace(space.id)}
                      />
                      <Label htmlFor={space.id} className="cursor-pointer flex-1">{space.name}</Label>
                    </div>
                  ))}
                </div>
                {formData.selectedSpaces.length > 0 && (
                  <p className="text-xs text-slate-500">{formData.selectedSpaces.length} space(s) selected</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={processing} className="rounded-full bg-slate-800 hover:bg-slate-700">
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : (editingHoliday ? 'Update Holiday' : 'Create Holiday')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="sm:max-w-[450px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bank Holiday?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {holidayToDelete && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <p className="font-medium text-slate-800">{holidayToDelete.name}</p>
              <p className="text-sm text-slate-500 mt-1">{formatDate(holidayToDelete.date)}</p>
              {holidayToDelete.project_id ? (
                <Badge className="mt-2 bg-sky-50 text-sky-700 border-sky-200">
                  <Building2 className="h-3 w-3 mr-1" />
                  {holidayToDelete.space_name || getSpaceName(holidayToDelete.project_id)}
                </Badge>
              ) : (
                <Badge className="mt-2 bg-violet-50 text-violet-700 border-violet-200">
                  <Globe className="h-3 w-3 mr-1" />
                  Company-Wide
                </Badge>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-full bg-rose-600 hover:bg-rose-700">
              Delete Holiday
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* UK Regions Dialog */}
      <Dialog open={regionsDialogOpen} onOpenChange={setRegionsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>UK Bank Holiday Regions</DialogTitle>
            <DialogDescription>
              Select which UK regions' bank holidays to include. Synced daily from Gov.uk.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {ukRegions.map((region) => (
              <div
                key={region.value}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedRegions.includes(region.value) ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => toggleRegion(region.value)}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedRegions.includes(region.value) ? 'bg-slate-800 border-slate-800' : 'border-slate-300'
                }`}>
                  {selectedRegions.includes(region.value) && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{region.label}</p>
                  <p className="text-xs text-slate-500">
                    {region.value === 'england-and-wales' && 'Includes England and Wales bank holidays'}
                    {region.value === 'scotland' && "Includes Scottish bank holidays (e.g., St Andrew's Day)"}
                    {region.value === 'northern-ireland' && "Includes NI bank holidays (e.g., St Patrick's Day)"}
                  </p>
                </div>
              </div>
            ))}

            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mt-4">
              <div className="flex gap-3">
                <RefreshCw className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-emerald-800 text-sm">Automatic Sync</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    Bank holidays sync from Gov.uk daily at 3 AM UTC. New holidays are added automatically.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegionsDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleUpdateRegions} disabled={processing || selectedRegions.length === 0} className="rounded-full bg-slate-800 hover:bg-slate-700">
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Check className="h-4 w-4 mr-2" />Save Regions</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
